import React, { useState } from 'react';
import { CircleDollarSign, Plus, Edit2, AlertCircle, Save, Percent } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { pricingPlans, creditPacks } from "@/features/sonas/mockData";

const EconomySettings = () => {
  const [rates, setRates] = useState({
    standard: 0.06,
    complex: 0.15,
    studio_sync: 0.02
  });

  const handleSaveRates = () => {
    toast.success("Economy rates updated successfully.");
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Economy Management</h1>
        <p className="text-sonas-muted text-xs mt-1">Configure credit burn rates and store inventory.</p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-sonas-dim">Burn Rates (USD/Token)</h3>
          <div className="bg-sonas-surface border border-sonas-border rounded-2xl p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-sonas-muted">Standard Prompt</label>
              <div className="flex gap-2">
                <Input 
                  type="number" step="0.001" 
                  value={rates.standard}
                  onChange={(e) => setRates({...rates, standard: e.target.value})}
                  className="bg-sonas-surface2 border-sonas-border font-mono h-11"
                />
                <div className="h-11 w-11 shrink-0 rounded-lg bg-sonas-surface2 border border-sonas-border flex items-center justify-center">
                  <Percent size={14} className="text-sonas-dim" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase text-sonas-muted">Complex Task (Thinking Mode)</label>
              <Input 
                type="number" step="0.001" 
                value={rates.complex}
                onChange={(e) => setRates({...rates, complex: e.target.value})}
                className="bg-sonas-surface2 border-sonas-border font-mono h-11"
              />
            </div>
            <Button onClick={handleSaveRates} className="w-full bg-sonas-accent hover:bg-sonas-accent2 text-sonas-bg font-bold rounded-xl h-11 gap-2">
              <Save size={18} /> Save Burn Rates
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-sonas-dim">Active Credit Packs</h3>
          <div className="space-y-3">
            {creditPacks.map(pack => (
              <div key={pack.id} className="bg-sonas-surface border border-sonas-border p-4 rounded-xl flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold">{pack.label}</p>
                  <p className="text-[10px] text-sonas-dim">{pack.credits}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-mono font-bold text-sonas-accent">{pack.price}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-white/5">
                    <Edit2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" className="w-full border-dashed border-sonas-border hover:border-sonas-accent hover:bg-sonas-accent/5 rounded-xl h-12 text-sonas-dim gap-2">
              <Plus size={18} /> Add New Pack
            </Button>
          </div>
        </div>
      </div>

      <section className="bg-red-500/5 border border-red-500/20 p-6 rounded-2xl flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 shrink-0">
          <AlertCircle size={24} />
        </div>
        <div>
          <h4 className="font-syne font-bold text-red-500">Economy Threshold Alert</h4>
          <p className="text-sm text-sonas-muted mt-1">
            Current credit burn rate is 15% higher than user top-ups this week. Consider adjusting rates or offering a promo code.
          </p>
          <Button variant="outline" className="mt-4 border-red-500/20 text-red-500 hover:bg-red-500/10 rounded-lg text-xs font-bold px-4 h-8">
            Manage Fraud Alerts
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EconomySettings;